import { useState } from 'react';
import { useApi } from '../lib/useApi.ts';
import { money, percent } from '../lib/format.ts';
import { Badge, Card, EmptyState, Skeleton, TBody, TD, TH, THead, TR, Table } from '../components/ui/index.ts';
import { cx } from '../lib/cx.ts';

type Grain = 'call' | 'people' | 'money' | 'mixed';

interface Metric {
  key: string;
  label: string;
  value: string | number | null;
  grain: Grain;
  format: 'count' | 'percent' | 'money' | 'days' | 'decimal';
  hint?: string | null;
}

interface MetricGroup {
  key: string;
  label: string;
  anchor: string;
  metrics: Metric[];
}

interface Response {
  groups: MetricGroup[];
  showRate: { call: Metric; people: Metric };
  bySource: { source: string; leads: number; ran: number; won: number; collected: string }[];
}

const RANGES = [
  { id: '30', label: 'Last 30 days' },
  { id: '90', label: 'Last 90 days' },
  { id: '365', label: 'Last year' },
  { id: 'all', label: 'All time' },
];

/**
 * CLAUDE.md non-negotiable 2: people metrics dedupe by lead, call metrics
 * count calls, and the two are never mixed in one tile. Every tile is
 * therefore stamped with its grain, so a number cannot be misread at a
 * glance.
 */
const GRAIN: Record<Grain, { label: string; tone: 'open' | 'accent' | 'won' | 'risk' }> = {
  call: { label: 'per call', tone: 'open' },
  people: { label: 'per person', tone: 'accent' },
  money: { label: 'money', tone: 'won' },
  mixed: { label: 'mixed grain', tone: 'risk' },
};

function formatValue(metric: Metric): string {
  if (metric.value === null) return '—';
  switch (metric.format) {
    case 'percent':
      return percent(Number(metric.value), 1);
    case 'money':
      return money(metric.value as string);
    case 'days':
      return `${Number(metric.value).toFixed(1)}d`;
    case 'decimal':
      return Number(metric.value).toFixed(1);
    default:
      return String(metric.value);
  }
}

export default function Metrics() {
  const [range, setRange] = useState('90');
  const [source, setSource] = useState('');

  const params = new URLSearchParams();
  if (range !== 'all') {
    const from = new Date(Date.now() - Number(range) * 86_400_000);
    params.set('from', from.toISOString());
  }
  if (source) params.set('source', source);

  const { data, error, loading } = useApi<Response>(`/metrics?${params.toString()}`);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tighter">Metrics</h1>
          <p className="mt-0.5 text-xs text-fg-subtle">
            Every tile says whether it counts calls or people. They are never the same number.
          </p>
        </div>
        <div className="flex gap-2">
          <select
            value={range}
            onChange={(event) => setRange(event.target.value)}
            className="h-8 rounded-md border border-border bg-surface px-2 text-sm text-fg focus:border-border-strong"
          >
            {RANGES.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            value={source}
            onChange={(event) => setSource(event.target.value)}
            className="h-8 rounded-md border border-border bg-surface px-2 text-sm text-fg focus:border-border-strong"
          >
            <option value="">All sources</option>
            <option value="webinar">Webinar</option>
            <option value="ad">Ad</option>
            <option value="outreach">Outreach</option>
            <option value="referral">Referral</option>
            <option value="unknown">Unknown</option>
          </select>
        </div>
      </header>

      {error && <p className="text-sm text-lost">{error}</p>}
      {loading && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }, (_, index) => (
            <Skeleton key={index} className="h-28 w-full" />
          ))}
        </div>
      )}

      {/* The one comparison worth putting at the top: the same idea measured
          two ways, deliberately shown together so neither is mistaken for
          the other. */}
      {!loading && data && (
        <Card>
          <div className="flex flex-wrap items-start gap-8">
            <div>
              <p className="text-xs text-fg-subtle">Show rate</p>
              <p className="numeric mt-1 text-3xl font-semibold tracking-tighter">
                {formatValue(data.showRate.call)}
              </p>
              <div className="mt-1.5 flex items-center gap-2">
                <Badge tone="open">per call</Badge>
                <span className="text-xs text-fg-subtle">{data.showRate.call.hint}</span>
              </div>
            </div>
            <div>
              <p className="text-xs text-fg-subtle">Show rate</p>
              <p className="numeric mt-1 text-3xl font-semibold tracking-tighter">
                {formatValue(data.showRate.people)}
              </p>
              <div className="mt-1.5 flex items-center gap-2">
                <Badge tone="accent">per person</Badge>
                <span className="text-xs text-fg-subtle">{data.showRate.people.hint}</span>
              </div>
            </div>
            <p className="max-w-sm text-xs leading-relaxed text-fg-subtle">
              These differ on purpose. Someone who no-showed once and then showed counts as one
              of each on the left, and as reached on the right.
            </p>
          </div>
        </Card>
      )}

      {!loading &&
        data?.groups.map((group) => (
          <section key={group.key}>
            <div className="mb-3 flex items-baseline gap-2">
              <h2 className="text-sm font-medium text-fg">{group.label}</h2>
              <span className="text-2xs text-fg-subtle">date range applies to {group.anchor}</span>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {group.metrics.map((metric) => (
                <div
                  key={metric.key}
                  className="surface-lift rounded-lg border border-border bg-surface p-4"
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-xs font-medium text-fg-subtle">{metric.label}</p>
                    <Badge tone={GRAIN[metric.grain].tone}>{GRAIN[metric.grain].label}</Badge>
                  </div>
                  <p
                    className={cx(
                      'numeric mt-2 font-semibold tracking-tighter text-fg',
                      metric.format === 'money' ? 'text-2xl' : 'text-3xl',
                    )}
                  >
                    {formatValue(metric)}
                  </p>
                  {metric.hint && <p className="mt-1.5 text-xs text-fg-subtle">{metric.hint}</p>}
                </div>
              ))}
            </div>
          </section>
        ))}

      {!loading && data && data.bySource.length > 0 && (
        <section>
          <h2 className="mb-3 text-sm font-medium text-fg">By source</h2>
          <Card flush>
            <Table>
              <THead>
                <TH>Source</TH>
                <TH numeric>Leads</TH>
                <TH numeric>Calls ran</TH>
                <TH numeric>Won</TH>
                <TH numeric>Collected</TH>
                <TH numeric>Revenue per lead</TH>
              </THead>
              <TBody>
                {data.bySource.map((row) => (
                  <TR key={row.source}>
                    <TD>
                      <span className="capitalize">{row.source}</span>
                    </TD>
                    <TD numeric>{row.leads}</TD>
                    <TD numeric muted>{row.ran}</TD>
                    <TD numeric>{row.won}</TD>
                    <TD numeric>{money(row.collected)}</TD>
                    <TD numeric muted>
                      {row.leads > 0 ? money(Number(row.collected) / row.leads) : '—'}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </Card>
          <p className="mt-2 text-2xs text-fg-subtle">
            Leads and Won count people. Calls ran counts calls. They are different denominators.
          </p>
        </section>
      )}

      {!loading && !data && !error && (
        <EmptyState title="No metrics yet" description="Seed the database or log a call to see numbers here." />
      )}
    </div>
  );
}
